import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { useCallback, useState } from 'react';
import { Box, Card, CardHeader } from '@mui/material';
import { apply_setter_to_array, get_z3k_object_state, HeatingCircuit, RadioModule, RadioSensor, WiredTemperatureSensor, } from 'z3k-config';
import { useDispatch } from 'react-redux';
import { Map as ImmutableMap, Set as ImmutableSet } from 'immutable';
import { send_z3k_command } from 'z3k-settings/redux/actions';
import { unpack_validation, with_root_as_2nd_arg, } from 'webui/Z3KConfigParser/validation/types';
import { minMaxStr } from 'webui/Z3KConfigParser/validation/basic';
import { validate_by_specification, validate_map, } from 'webui/Z3KConfigParser/validation/collections';
import { HeatingControlType } from './type';
import { SensorsTable } from './digital-sensors/sensors-table';
import { RadioRegistrationButton } from './digital-sensors/registration-button';
import { useConfirmDialog } from 'shared/hooks/ui/confirm-dialog';
import { ClimaticDigitalSensorType } from 'console/climatic/setting-tabs/digital-sensor-type';
export function default_settings_from_sensor(sensor, type, circuit_options) {
    return {
        type,
        sensor_id: sensor.id,
        name: sensor.name,
        serial: sensor.serial,
        circuit_id: circuit_options.find((co) => co.air_temp_sensor == sensor.id)
            ?.circuit_id,
    };
}
const DigitalSensorsPage = ({ tab_key, state, validation, volatile_data, onChangeState, }) => {
    const dispatch = useDispatch();
    const onDeleteSensor = useCallback((sensor_id) => {
        dispatch(send_z3k_command(state.digital_sensors.device_id, sensor_id, 'RemoveObject', null, null, false));
    }, [dispatch, state.digital_sensors.device_id]);
    const this_tab_validation = unpack_validation(validation, 'digital_sensors');
    const sensors_validation = unpack_validation(this_tab_validation, 'sensor_settings');
    const on_change = (sensors) => {
        let new_sensor_settings = state.digital_sensors.sensor_settings;
        for (const settings of sensors)
            new_sensor_settings = new_sensor_settings.set(settings.sensor_id, settings);
        onChangeState(tab_key, { sensor_settings: new_sensor_settings });
    };
    const [onClickId, setOnClickId] = useState(null);
    const [sensor_type, setSensorType] = useState(ClimaticDigitalSensorType.WIRED);
    const [ConfirmDialog, openConfirmDialog] = useConfirmDialog();
    const _openConfirmDialog = useCallback((sensor_id, sensor_type) => {
        setOnClickId(sensor_id);
        setSensorType(sensor_type);
        openConfirmDialog();
    }, [setOnClickId, openConfirmDialog, setSensorType]);
    const deleteSensor = useCallback(() => {
        if (onClickId) {
            onDeleteSensor(onClickId);
            onChangeState(tab_key, {
                sensor_settings: state.digital_sensors.sensor_settings.remove(onClickId),
                deleted_sensor_ids: state.digital_sensors.deleted_sensor_ids.add(onClickId),
            });
        }
    }, [state.digital_sensors, onDeleteSensor, onClickId]);
    return (_jsxs(Box, { display: "flex", flexDirection: "column", sx: { gridRowGap: '1em' }, children: [state.digital_sensors.radiomodule != null && (_jsxs(Card, { variant: "outlined", children: [_jsxs(Box, { display: "flex", justifyContent: "space-between", alignItems: "center", pr: 2, children: [_jsx(CardHeader, { title: "\u0420\u0430\u0434\u0438\u043E\u0434\u0430\u0442\u0447\u0438\u043A\u0438" }), _jsx(RadioRegistrationButton, { enabled: volatile_data.online, device_id: state.digital_sensors.device_id, module_id: state.digital_sensors.radiomodule?.id, module_state: get_z3k_object_state(volatile_data.z3k_state, state.digital_sensors.radiomodule?.id) })] }), _jsx(Box, { px: 2, pb: 2, children: _jsx(SensorsTable, { sensors: volatile_data.radiosensors
                                .filter(({ id }) => !state.digital_sensors.deleted_sensor_ids.has(id))
                                .map((radiosensor) => {
                                const settings = state.digital_sensors.sensor_settings.get(radiosensor.id);
                                return (settings ??
                                    default_settings_from_sensor(radiosensor, ClimaticDigitalSensorType.RADIO, state.circuit_options));
                            }), validation: volatile_data.radiosensors.map((radiosensor) => unpack_validation(sensors_validation, radiosensor.id)), circuit_options: state.circuit_options, predicate: (sensor) => sensor.type == ClimaticDigitalSensorType.RADIO, z3k_state: volatile_data.z3k_state, delete_enabled: volatile_data.online, onChange: on_change, onDelete: _openConfirmDialog }) })] })), _jsxs(Card, { variant: "outlined", children: [_jsx(CardHeader, { title: "\u041F\u0440\u043E\u0432\u043E\u0434\u043D\u044B\u0435 \u0434\u0430\u0442\u0447\u0438\u043A\u0438" }), _jsx(Box, { px: 2, pb: 2, children: _jsx(SensorsTable, { sensors: volatile_data.wired_temperature_sensors
                                .filter(({ id }) => !state.digital_sensors.deleted_sensor_ids.has(id))
                                .map((sensor) => {
                                const settings = state.digital_sensors.sensor_settings.get(sensor.id);
                                return {
                                    ...(settings ??
                                        default_settings_from_sensor(sensor, ClimaticDigitalSensorType.WIRED, state.circuit_options)),
                                    ...(sensor.serial && { serial: sensor.serial }),
                                };
                            }), validation: volatile_data.wired_temperature_sensors.map((sensor) => unpack_validation(sensors_validation, sensor.id)), circuit_options: state.circuit_options, predicate: (sensor) => sensor.type == ClimaticDigitalSensorType.WIRED, z3k_state: volatile_data.z3k_state, delete_enabled: volatile_data.online, onChange: on_change, onDelete: _openConfirmDialog }) })] }), _jsx(ConfirmDialog, { onConfirm: deleteSensor, children: sensor_type == ClimaticDigitalSensorType.RADIO
                    ? 'Чтобы подключить его заново, потребуется физический доступ к датчику'
                    : 'Чтобы вернуть датчик, достаточно будет подключить его к прибору' })] }));
};
export const digital_sensors_tab = {
    key: 'digital_sensors',
    title: 'Цифровые термодатчики',
    element(props) {
        return _jsx(DigitalSensorsPage, { ...props });
    },
    state_subset: (all_tabs_state, _tab_key) => {
        const control_mode_blacklist = [
            HeatingCircuit.ControlMode.EXT_THERMOSTAT,
            HeatingCircuit.ControlMode.WATER,
            HeatingCircuit.ControlMode.PZA,
        ];
        return {
            digital_sensors: all_tabs_state.digital_sensors,
            circuit_options: Object.entries(all_tabs_state)
                .filter(([key, _tab_state]) => key.startsWith('circuit-'))
                .map(([_key, tab_state]) => tab_state)
                .filter((tab_state) => tab_state.circuit.type == HeatingCircuit.Type.HEATING)
                .filter((tab_state) => all_tabs_state.configuration.circuits.some((settings) => settings.id == tab_state.circuit.id &&
                settings.control_type !== HeatingControlType.OFF))
                .filter((tab_state) => tab_state.control_mode != null &&
                !control_mode_blacklist.includes(tab_state.control_mode))
                .map((tab_state) => ({
                circuit_id: tab_state.circuit.id,
                name: tab_state.circuit.name,
                air_temp_sensor: tab_state.circuit.air_temp_sensor,
            })),
        };
    },
    isShown: (state, volatile_data) => volatile_data.service_mode,
    data2state: ({ device }) => {
        const circuit_options = HeatingCircuit.get
            .all(device.z3k_config)
            .map((circuit) => ({
            circuit_id: circuit.id,
            name: circuit.name,
            air_temp_sensor: circuit.air_temp_sensor,
        }));
        return {
            device_id: device.id,
            radiomodule: RadioModule.get.all(device.z3k_config)[0],
            sensor_settings: ImmutableMap([
                ...WiredTemperatureSensor.get
                    .all(device.z3k_config)
                    .map((sensor) => [
                    sensor.id,
                    default_settings_from_sensor(sensor, ClimaticDigitalSensorType.WIRED, circuit_options),
                ]),
                ...RadioSensor.get
                    .where(device.z3k_config, (s) => s.type != RadioSensor.Type.LEAK)
                    .map((sensor) => [
                    sensor.id,
                    default_settings_from_sensor(sensor, ClimaticDigitalSensorType.RADIO, circuit_options),
                ]),
            ]),
            deleted_sensor_ids: ImmutableSet(),
        };
    },
    validate: with_root_as_2nd_arg(validate_by_specification({
        digital_sensors: validate_by_specification({
            sensor_settings: validate_map((sensor_id, settings, global_state) => validate_by_specification({
                name: WiredTemperatureSensor.isRealSensor(settings)
                    ? minMaxStr(1, 10)
                    : minMaxStr(1, 20),
                circuit_id: (value, root) => Array.from(root.digital_sensors.sensor_settings.values()).filter((sensor_settings) => sensor_settings.circuit_id != null &&
                    sensor_settings.circuit_id == value).length > 1
                    ? 'Назначен один и тот же контур'
                    : null,
            })(settings, global_state)),
        }),
    })),
    state2data: (state) => (setter) => {
        const z3k_config = { ...setter.z3k_config };
        for (const settings of state.digital_sensors.sensor_settings.values()) {
            z3k_config.wired_temperature_sensors = apply_setter_to_array(WiredTemperatureSensor.get.where(z3k_config, WiredTemperatureSensor.isRealSensor), settings.sensor_id, { name: settings.name });
            z3k_config.radiosensors = apply_setter_to_array(RadioSensor.get.all(z3k_config), settings.sensor_id, { name: settings.name });
        }
        state.digital_sensors.sensor_settings.forEach((setting) => {
            if (setting.circuit_id != null &&
                state.circuit_options.some((option) => option.circuit_id == setting.circuit_id))
                z3k_config.heating_circuits = apply_setter_to_array(HeatingCircuit.get.all(z3k_config), setting.circuit_id, { air_temp_sensor: setting.sensor_id });
            else
                z3k_config.heating_circuits = HeatingCircuit.get
                    .all(z3k_config)
                    .map((hc) => hc.air_temp_sensor == setting.sensor_id
                    ? { ...hc, air_temp_sensor: null }
                    : hc);
        });
        return { ...setter, z3k_config };
    },
};
